export type TtsVoiceGenderLabel = '남성' | '여성';
export type TtsVoiceFilterKey = 'all' | 'high' | 'normal' | 'male' | 'female';

export interface VoiceListItem {
  code: string;
  name: string;
  nameKorean: string;
  description: string;
  descriptionKorean: string;
  vendor: string;
}

const MALE_TAGS = new Set(['#남성', '#male']);
const FEMALE_TAGS = new Set(['#여성', '#female']);
const GENDER_SEARCH_LABELS = new Map<string, TtsVoiceGenderLabel>([
  ['male', '남성'],
  ['남성', '남성'],
  ['female', '여성'],
  ['여성', '여성'],
]);
export const PremiumVendors: readonly string[] = [
  'typecast',
  'elevenlabs',
  'gemini',
];

export function readTtsVoiceGenderLabel(
  voice: Pick<VoiceListItem, 'description' | 'descriptionKorean'>,
): TtsVoiceGenderLabel | null {
  const tags =
    `${voice.descriptionKorean ?? ''} ${voice.description ?? ''}`
      .match(/#[^\s#]+/g)
      ?.map((tag) => tag.toLowerCase()) ?? [];

  if (tags.some((tag) => FEMALE_TAGS.has(tag))) return '여성';
  if (tags.some((tag) => MALE_TAGS.has(tag))) return '남성';
  return null;
}

export function formatTtsVoiceSimpleLabel(
  voice: Pick<
    VoiceListItem,
    'code' | 'name' | 'nameKorean' | 'description' | 'descriptionKorean'
  >,
) {
  const name = voice.nameKorean || voice.name || voice.code;
  const tags = (voice.descriptionKorean || voice.description || '').trim();
  return tags ? `${name} / ${tags}` : name;
}

export function isPremiumTtsVoice(
  voice: Pick<VoiceListItem, 'vendor'>,
): boolean {
  return PremiumVendors.includes(voice.vendor);
}

export function applyTtsVoiceFilter<T extends VoiceListItem>(
  voices: readonly T[],
  filterKey: TtsVoiceFilterKey,
): T[] {
  if (filterKey === 'all') return [...voices];
  if (filterKey === 'high') {
    return voices.filter((voice) => isPremiumTtsVoice(voice));
  }
  if (filterKey === 'normal') {
    return voices.filter((voice) => !isPremiumTtsVoice(voice));
  }

  const genderLabel = filterKey === 'female' ? '여성' : '남성';
  return voices.filter(
    (voice) => readTtsVoiceGenderLabel(voice) === genderLabel,
  );
}

export function applyTtsVoiceFilters<T extends VoiceListItem>(
  voices: readonly T[],
  filterKeys: readonly TtsVoiceFilterKey[],
): T[] {
  const uniqueFilterKeys = Array.from(new Set(filterKeys));
  if (uniqueFilterKeys.length === 0) return [...voices];
  if (uniqueFilterKeys.includes('all')) return [...voices];

  return voices.filter((voice) =>
    uniqueFilterKeys.some(
      (filterKey) => applyTtsVoiceFilter([voice], filterKey).length > 0,
    ),
  );
}

export function filterTtsVoicesBySearch<T extends VoiceListItem>(
  voices: readonly T[],
  searchQuery: string,
): T[] {
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  if (!normalizedQuery) return [...voices];

  const genderLabel = GENDER_SEARCH_LABELS.get(normalizedQuery);
  if (genderLabel) {
    return voices.filter(
      (voice) => readTtsVoiceGenderLabel(voice) === genderLabel,
    );
  }

  return voices.filter((voice) =>
    [
      voice.code,
      voice.name,
      voice.nameKorean,
      voice.description,
      voice.descriptionKorean,
      voice.vendor,
    ]
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedQuery),
  );
}

export function sortTtsVoicesForPicker<T extends VoiceListItem>(
  voices: readonly T[],
): T[] {
  return voices
    .map((voice, index) => ({ voice, index }))
    .sort((left, right) => {
      const premiumDelta =
        Number(isPremiumTtsVoice(right.voice)) -
        Number(isPremiumTtsVoice(left.voice));

      return premiumDelta || left.index - right.index;
    })
    .map(({ voice }) => voice);
}

export function splitRecommendedTtsVoices<T extends VoiceListItem>(
  voices: readonly T[],
  recommendedVoiceCodes: readonly string[],
): { recommended: T[]; remaining: T[] } {
  const voicesByCode = new Map(voices.map((voice) => [voice.code, voice]));
  const recommended: T[] = [];
  const recommendedCodes = new Set<string>();

  for (const code of recommendedVoiceCodes) {
    const voice = voicesByCode.get(code);
    if (!voice || recommendedCodes.has(code)) continue;
    recommended.push(voice);
    recommendedCodes.add(code);
  }

  const remaining = voices.filter((voice) => !recommendedCodes.has(voice.code));

  return {
    recommended,
    remaining: sortTtsVoicesForPicker(remaining),
  };
}
